import React, { useEffect, useState } from "react";
import {
  Box,
  Spinner,
  Text,
  Card,
  Flex,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  VStack,
  Badge,
  Link,
  useToast,
} from "@chakra-ui/react";
import { MdOpenInNew } from "react-icons/md";
import { useParams } from "react-router-dom";
import axiosInstance from "../routes/axiosInstance";
import { useAuth } from "../context/AuthContext";
import { slugifyText } from "../logics/spaceLogic";
import BackButton from "../components/BackButton";
import OverviewTab from "../components/spaces/admin/OverviewTab";
import ParticipantsTab from "../components/spaces/admin/ParticipantsTab";
import ReportTab from "../components/spaces/admin/ReportTab";

const EditSpacePage = () => {
  const { spaceId } = useParams();
  const { currentUser, checkUserAuth } = useAuth();
  const toast = useToast();
  const [space, setSpace] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [members, setMembers] = useState([]);
  const [activeTabIndex, setActiveTabIndex] = useState(0);

  useEffect(() => {
    const fetchSpace = async () => {
      if (!currentUser?.token) {
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);
      try {
        const config = {
          headers: {
            "Content-type": "application/json",
            Authorization: `Bearer ${currentUser.token}`,
          },
        };
        const res = await axiosInstance.get(`/api/spaces/${spaceId}`, config);
        setSpace(res.data.data);
        if (res.data.data.members) {
          setMembers(res.data.data.members);
        }
      } catch (err) {
        const errorMessage =
          err.response?.data?.message || err.message || "Failed to fetch space";
        setError(errorMessage);
        toast({
          title: "Error",
          description: errorMessage,
          status: "error",
          duration: 3000,
          isClosable: true,
        });
        checkUserAuth(err.response?.status);
      } finally {
        setLoading(false);
      }
    };
    fetchSpace();
  }, [spaceId, currentUser?.token, checkUserAuth, toast]);

  const handleSpaceUpdate = (updatedSpace) => {
    setSpace((prevSpace) => ({ ...prevSpace, ...updatedSpace }));
  };

  const handleMemberStatusUpdate = (memberId, newStatus) => {
    setMembers((prevMembers) =>
      prevMembers.map((member) =>
        member._id === memberId || member.id === memberId
          ? { ...member, isActive: newStatus }
          : member,
      ),
    );
  };

  const handleOpenChatUrl = () => {
    if (!space || !space.referenceId || !space.owner?.guestId) return;
    const chatUrl = `${window.location.origin}/s/${
      space.referenceId
    }/${slugifyText(space.title)}?gid=${space.owner.guestId}`;
    window.open(chatUrl, "_blank");
  };

  // Show loading if no user is available
  if (!currentUser) {
    return (
      <Box bg="gray.50" minH="100vh">
        <Flex direction="column" h="100vh" maxW="md" mx="auto">
          <Flex align="center" justify="center" flex="1">
            <VStack spacing={3}>
              <Spinner size="lg" color="teal.500" />
              <Text fontSize="sm" color="gray.500">
                Loading...
              </Text>
            </VStack>
          </Flex>
        </Flex>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box bg="gray.50" minH="100vh">
        <Flex direction="column" h="100vh" maxW="md" mx="auto">
          <Flex align="center" justify="center" flex="1">
            <VStack spacing={3}>
              <Spinner size="lg" color="teal.500" />
              <Text fontSize="sm" color="gray.500">
                Loading space details...
              </Text>
            </VStack>
          </Flex>
        </Flex>
      </Box>
    );
  }

  if (error) {
    return (
      <Box bg="gray.50" minH="100vh">
        <Flex direction="column" h="100vh" maxW="md" mx="auto">
          <Flex align="center" justify="center" flex="1">
            <VStack spacing={3} textAlign="center" p={4}>
              <Text fontSize="lg" color="red.500" fontWeight="600">
                Error Loading Space
              </Text>
              <Text fontSize="sm" color="gray.500">
                {error}
              </Text>
            </VStack>
          </Flex>
        </Flex>
      </Box>
    );
  }

  if (!space) return null;

  return (
    <Box bg="gray.50" minH="100vh">
      <Flex direction="column" h="100vh" maxW="md" mx="auto">
        {/* Header */}
        <Card
          bg="white"
          borderRadius="none"
          borderBottom="1px solid"
          borderColor="gray.200"
          p={3}
          boxShadow="sm"
        >
          <VStack spacing={2} align="stretch">
            <Flex align="center">
              <BackButton link="/spaces" />
              <Text
                fontSize="lg"
                fontWeight="600"
                color="gray.800"
                ml={3}
                noOfLines={1}
                flex="1"
              >
                {space.title}
              </Text>
              <Badge
                colorScheme="teal"
                fontSize="xs"
                px={2}
                py={1}
                borderRadius="full"
                ml={2}
              >
                {space.kind}
              </Badge>
            </Flex>

            {space.kind === "CHAT" &&
              space.referenceId &&
              space.owner?.guestId && (
                <Flex justify="flex-end">
                  <Link
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      handleOpenChatUrl();
                    }}
                    color="blue.500"
                    fontSize="sm"
                    fontWeight="500"
                    display="flex"
                    alignItems="center"
                    _hover={{
                      color: "blue.600",
                      textDecoration: "underline",
                    }}
                  >
                    <MdOpenInNew style={{ marginRight: "4px" }} />
                    Open Chat
                  </Link>
                </Flex>
              )}
          </VStack>
        </Card>

        {/* Content */}
        <Box
          flex="1"
          bg="white"
          borderLeft="1px solid"
          borderRight="1px solid"
          borderColor="gray.200"
          overflowY="auto"
        >
          <Box p={4}>
            <Tabs
              colorScheme="teal"
              variant="enclosed"
              index={activeTabIndex}
              onChange={setActiveTabIndex}
            >
              <TabList bg="gray.50" borderRadius="lg" p={1}>
                <Tab
                  borderRadius="md"
                  _selected={{
                    bg: "teal.500",
                    color: "white",
                    borderColor: "teal.500",
                  }}
                  _hover={{ bg: "teal.100" }}
                  fontWeight="600"
                  fontSize="sm"
                >
                  Overview
                </Tab>
                <Tab
                  borderRadius="md"
                  _selected={{
                    bg: "teal.500",
                    color: "white",
                    borderColor: "teal.500",
                  }}
                  _hover={{ bg: "teal.100" }}
                  fontWeight="600"
                  fontSize="sm"
                >
                  Participants ({members.length})
                </Tab>
                <Tab
                  borderRadius="md"
                  _selected={{
                    bg: "teal.500",
                    color: "white",
                    borderColor: "teal.500",
                  }}
                  _hover={{ bg: "teal.100" }}
                  fontWeight="600"
                  fontSize="sm"
                >
                  Report
                </Tab>
              </TabList>

              <TabPanels mt={4}>
                <TabPanel px={0}>
                  <OverviewTab
                    space={space}
                    currentUser={currentUser}
                    onSpaceUpdate={handleSpaceUpdate}
                  />
                </TabPanel>

                <TabPanel px={0}>
                  <ParticipantsTab
                    members={members}
                    spaceId={spaceId}
                    onMemberStatusUpdate={handleMemberStatusUpdate}
                    currentUser={currentUser}
                  />
                </TabPanel>

                <TabPanel px={0}>
                  <ReportTab
                    space={space}
                    currentUser={currentUser}
                    isActive={activeTabIndex === 2}
                  />
                </TabPanel>
              </TabPanels>
            </Tabs>
          </Box>
        </Box>
      </Flex>
    </Box>
  );
};

export default EditSpacePage;
